/**
 * The terms gate. One version, one acceptance, one button.
 *
 * THE DOCUMENT IS NOT OURS TO RENDER. `contentUrl` points at moonx.app — a DIFFERENT ORIGIN from
 * the cabinet — and the platform stores the URL plus a sha256 of the bytes accepted and never the
 * text. So this page LINKS OUT in a new tab and must never try to embed or fetch it; a fetch would
 * be blocked by CSP anyway and an iframe would let us render something the record does not cover.
 *
 * ONE ACCEPTANCE PER VERSION, NOT FOUR CHECKBOXES. The prototype draws four "Editorial standards"
 * checkboxes. The platform records ONE acceptance per author per terms version
 * (`UNIQUE (author_id, terms_version_id)`), so four booleans would be a table we do not have. The
 * four statements belong inside the document; how this gate is laid out and worded is ours.
 *
 * TWO AUDIENCES, ONE SCREEN. An applicant on the way to sending, and one of the ~530 grandfathered
 * authors meeting the one-time cutover interstitial raised by `AuthorSession.termsAcceptanceRequired`.
 * Nobody is ever credited with an acceptance they did not make, so the existing authors are ASKED
 * rather than back-filled. The server records which channel it was; the client does not send it.
 *
 * AND THE ONE THING THAT MUST BE SAID OUT LOUD: the author agreement is a SUBMISSION document - a
 * stale acceptance holds back sending for review and NEVER a payout (only design 25's payout
 * documents and Payment details can, from a date). Withholding money already earned over an
 * unclicked checkbox is not what this does, and an author reading a gate will assume it is.
 *
 * DRAWN AS THE PROFILE PAGE IS (the owner's live look at real cabinets, 2026-10-01 / design 15): a
 * reading column with the page's one `<h1>` at the prototype's title size, and the version, the
 * link and the button in one calm card. The frame around it - bare inside the cabinet, the stars
 * outside it - is the profile page's own (`PageFrame`, ../components/PageFrame.tsx).
 */
import { Anchor, Button, Group, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import type { TermsState } from '../contract';
import { ErrorState, LoadingState } from '../components/States';
import { humanMessage } from '../api/problem';
import classes from '../components/profile.module.css';

/** ISO instant to its UTC day, as the cabinet writes one (design 24, T16): never the browser's. */
function formatDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return formatDay(parsed.toISOString().slice(0, 10));
}

export interface TermsGateProps {
  readonly terms: TermsState | undefined;
  readonly loading: boolean;
  readonly error: unknown;
  readonly onRetry: () => void;
  readonly onAccept: () => void;
  readonly accepting: boolean;
  readonly acceptError: unknown;
  /** Where "not now" goes. Absent on the cutover interstitial, which has nowhere else to be. */
  readonly onBack?: (() => void) | undefined;
  /** Changes only the framing: an applicant is filling something in, an existing author is not. */
  readonly audience: 'applicant' | 'existing_author';
}

export function TermsGate({
  terms,
  loading,
  error,
  onRetry,
  onAccept,
  accepting,
  acceptError,
  onBack,
  audience,
}: TermsGateProps): ReactNode {
  const { t } = useTranslation('application');
  if (loading && terms === undefined) {
    return (
      <div className={classes.termsPage}>
        <LoadingState label={t('terms.loading')} lines={4} />
      </div>
    );
  }

  if (terms === undefined) {
    return (
      <div className={classes.termsPage}>
        <ErrorState error={error} fallback={t('terms.loadFailed')} onRetry={onRetry} />
      </div>
    );
  }

  const accepted = terms.accepted;
  const blocking = terms.blocksSubmission === true;

  return (
    <div className={classes.termsPage}>
      <Stack gap="lg">
        <div>
          <h1 className={classes.pageTitle}>
            {accepted
              ? t('terms.title')
              : audience === 'existing_author'
                ? t('terms.signOffTitle')
                : t('terms.title')}
          </h1>
          <p className={classes.lede}>
            {accepted
              ? t('terms.lede.accepted')
              : audience === 'existing_author'
                ? t('terms.lede.changed')
                : t('terms.lede.applicant')}
          </p>
        </div>

        <div className={classes.plainCard}>
          <Stack gap="md">
            <Group justify="space-between" wrap="wrap" gap="sm">
              <Stack gap={0}>
                <Text fw={600}>{terms.versionLabel}</Text>
                <Text size="xs" c="dimmed">
                  {t('terms.inEffectFrom', { date: formatDate(terms.effectiveFrom) })}
                </Text>
              </Stack>
              {terms.isMaterialChange ? (
                <Text size="xs" c="dimmed">
                  {t('terms.materialChange')}
                </Text>
              ) : null}
            </Group>

            <Anchor
              href={terms.contentUrl}
              target="_blank"
              rel="noopener noreferrer"
              // The document lives on another site of ours; the new tab is the honest behaviour and
              // is announced rather than sprung.
            >
              {t('terms.readInFull')}
            </Anchor>

            {accepted ? (
              <Stack gap={4}>
                <Text size="sm" style={{ color: 'oklch(var(--mx-success))' }}>
                  {terms.acceptedAt !== null && terms.acceptedAt !== undefined
                    ? t('terms.acceptedOn', { date: formatDate(terms.acceptedAt) })
                    : t('terms.accepted')}
                </Text>
                <Text size="xs" c="dimmed">
                  {t('terms.nothingToUndo')}
                </Text>
              </Stack>
            ) : (
              <Stack gap="sm">
                {blocking ? (
                  <Stack gap={4}>
                    <Text size="sm">{t('terms.blocking')}</Text>
                    <Text size="sm" fw={600}>
                      {t('terms.moneyNotHeld')}
                    </Text>
                  </Stack>
                ) : null}
                <Text size="xs" c="dimmed">
                  {t('terms.records')}
                </Text>
                {acceptError !== null && acceptError !== undefined ? (
                  <Text size="sm" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
                    {humanMessage(acceptError, t('terms.acceptFailed'), undefined, t)}
                  </Text>
                ) : null}
                <Group>
                  <Button loading={accepting} onClick={onAccept}>
                    {t('terms.accept')}
                  </Button>
                  {onBack !== undefined ? (
                    <Button variant="subtle" onClick={onBack} disabled={accepting}>
                      {t('agreement.back')}
                    </Button>
                  ) : null}
                </Group>
              </Stack>
            )}
          </Stack>
        </div>

        {accepted && onBack !== undefined ? (
          <Group>
            <Button variant="light" onClick={onBack}>
              {t('agreement.back')}
            </Button>
          </Group>
        ) : null}
      </Stack>
    </div>
  );
}

// The cabinet's day formatter (design 24, T16), imported at the foot so no line above moves.
import { formatDay } from '../../cabinet/format';
